import type { QueryClient } from '@tanstack/react-query'
import type { Issue } from '../types'

type IssuesResponse = {
  data?: {
    data?: Issue[]
  }
}

const sortIssues = (issues: Issue[]) =>
  issues
    .slice()
    .sort((a, b) => a.position - b.position || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

const getStatusFilter = (queryKey: readonly unknown[]) => {
  const filters = queryKey[2]
  if (!filters || typeof filters !== 'object') return ''

  const statusId = (filters as { statusId?: unknown }).statusId
  return typeof statusId === 'string' ? statusId : ''
}

const updateIssueList = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  updater: (issues: Issue[]) => Issue[]
) => {
  queryClient.setQueryData<IssuesResponse>(queryKey, (current) => {
    const issues = current?.data?.data
    if (!issues) return current

    return {
      ...current,
      data: {
        ...current.data,
        data: sortIssues(updater(issues)),
      },
    }
  })
}

export const insertIssueIntoProjectCaches = (queryClient: QueryClient, projectId: string, issue: Issue) => {
  for (const [queryKey, current] of queryClient.getQueriesData<IssuesResponse>({ queryKey: ['issues', projectId] })) {
    if (!current?.data?.data) continue

    const statusFilter = getStatusFilter(queryKey)
    if (statusFilter && statusFilter !== issue.statusId) continue

    updateIssueList(queryClient, queryKey, (issues) => [issue, ...issues.filter((existing) => existing.id !== issue.id)])
  }
}

export const syncIssueAcrossProjectCaches = (queryClient: QueryClient, projectId: string, issue: Issue) => {
  for (const [queryKey, current] of queryClient.getQueriesData<IssuesResponse>({ queryKey: ['issues', projectId] })) {
    if (!current?.data?.data) continue

    const statusFilter = getStatusFilter(queryKey)
    updateIssueList(queryClient, queryKey, (issues) => {
      const withoutCurrent = issues.filter((existing) => existing.id !== issue.id)
      if (statusFilter && statusFilter !== issue.statusId) {
        return withoutCurrent
      }

      return [issue, ...withoutCurrent]
    })
  }
}

export const prependIssueToSingleCache = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  issue: Issue
) => {
  updateIssueList(queryClient, queryKey, (issues) => [issue, ...issues.filter((existing) => existing.id !== issue.id)])
}
